import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy, addDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import BackButton from '../components/BackButton';
import Modal from '../components/Modal';
import {
  X,
  Check,
  Zap,
  Plus,
  SmilePlus,
  MoreVertical,
  Trash2,
  Loader2,
  CheckCircle,
  AlertTriangle,
  Grid3X3,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import {
  getAutomationGroupEmojis,
  saveAutomationGroupEmoji,
  deleteAutomationTemplate,
  saveAutomationsOrder,
  sortAutomationRows,
} from '../services/automationTemplatesService';
import PortalBackground from '../components/PortalBackground';
import BBALoader from '../components/BBALoader';
// Lazy-loaded so the full emoji picker (~large bundle) only downloads when opened.
const EmojiPicker = React.lazy(() => import('emoji-picker-react'));

const TABS = [
  {
    id: 'lead',
    label: 'Lead',
    emoji: '🎯',
    blurb: 'Automations for lead capture, follow-up, and nurturing.',
  },
  {
    id: 'design',
    label: 'Design',
    emoji: '🎨',
    blurb: 'Automations for the design and selections phase.',
  },
  {
    id: 'build',
    label: 'Build',
    emoji: '🏗️',
    blurb: 'Automations for production, scheduling, and job updates.',
  },
  {
    id: 'office',
    label: 'Office',
    emoji: '💼',
    blurb: 'Automations for back-office, finance, and admin work.',
  },
];

/**
 * CRM manager for automation templates. Mirrors the Imports / Sales collections
 * look: pill group tabs with the shared emoji picker, compact cards with a
 * version chip, new-version and history actions, and a kebab menu for delete.
 */
const AutomationTemplates = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();

  const [activeTab, setActiveTab] = useState('lead');
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [menuKey, setMenuKey] = useState(null);

  // Emoji picker — targets a group tab, a template card, the add form, or an add-form step
  const [groupEmojis, setGroupEmojis] = useState({});
  const [emojiTarget, setEmojiTarget] = useState(null);
  const [emojiSaving, setEmojiSaving] = useState(false);

  // Add-template modal
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', description: '', category: 'lead', emoji: '' });
  const [addSteps, setAddSteps] = useState([]);
  const [newStepTitle, setNewStepTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [addError, setAddError] = useState('');

  // App Connections modal (drives the portal "Connect Apps" list via localStorage)
  const [showAppConnectionsModal, setShowAppConnectionsModal] = useState(false);
  const [selectedApps, setSelectedApps] = useState([]);
  const [softwareCollections, setSoftwareCollections] = useState([]);

  const active = TABS.find((t) => t.id === activeTab) || TABS[0];
  const emojiOf = (tabId) => groupEmojis[tabId] || TABS.find((t) => t.id === tabId)?.emoji || '⚙️';
  const userName = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'BBA Team';

  const card = 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800';
  const subtle = darkMode ? 'text-gray-400' : 'text-gray-500';
  const chipGood =
    'inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
  const inputClass = `w-full rounded-xl border px-3 py-2 text-sm ${
    darkMode
      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
  }`;
  const labelClass = `block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`;

  useEffect(() => {
    setMenuKey(null);
    setError(null);
  }, [activeTab]);

  // Fetch templates for the active tab (manual sortOrder wins, unplaced newest first)
  const fetchAutomations = async (tabId) => {
    const automationsRef = collection(db, 'automations');
    const q = query(automationsRef, where('category', '==', tabId), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return sortAutomationRows(querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const rows = await fetchAutomations(activeTab);
        if (!cancelled) setAutomations(rows);
      } catch (e) {
        console.error('Error fetching automations:', e);
        if (!cancelled) {
          setAutomations([]);
          setError(e.message || 'Failed to load automation templates.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  const refreshList = async () => {
    const rows = await fetchAutomations(activeTab);
    setAutomations(rows);
  };

  useEffect(() => {
    getAutomationGroupEmojis().then(setGroupEmojis).catch(() => {});
  }, []);

  // Fetch software collections for the App Connections modal
  useEffect(() => {
    const fetchSoftwareData = async () => {
      try {
        const collectionNames = ['Core', 'Primary', 'Secondary'];
        const collections = [];
        for (const collectionName of collectionNames) {
          const typesSnapshot = await getDocs(collection(db, 'technologyStack', collectionName, 'types'));
          const types = [];
          typesSnapshot.forEach((typeDoc) => {
            const typeData = typeDoc.data();
            if (typeData && typeData.name) {
              types.push({
                id: typeDoc.id,
                name: typeData.name,
                tier: collectionName,
                description: typeData.description || '',
              });
            }
          });
          types.sort((a, b) => a.name.localeCompare(b.name));
          collections.push({ name: collectionName, types });
        }
        setSoftwareCollections(collections);

        const savedApps = localStorage.getItem('appConnections');
        if (savedApps) setSelectedApps(JSON.parse(savedApps));
      } catch (error) {
        console.error('Error fetching software types:', error);
      }
    };
    fetchSoftwareData();
  }, []);

  const allSoftwareTypes = softwareCollections.flatMap((c) => c.types);

  // ---- Emoji picking (group tab, card, add form, add-form step) ----
  const pickEmoji = async (emoji) => {
    if (!emojiTarget) return;
    // Form targets are local state only — no save needed
    if (emojiTarget.kind === 'form') {
      setAddForm((prev) => ({ ...prev, emoji }));
      setEmojiTarget(null);
      return;
    }
    if (emojiTarget.kind === 'form-step') {
      setAddSteps((prev) =>
        prev.map((s, i) => (i === emojiTarget.index ? { ...s, emoji } : s))
      );
      setEmojiTarget(null);
      return;
    }
    setEmojiSaving(true);
    setError(null);
    try {
      if (emojiTarget.kind === 'group') {
        await saveAutomationGroupEmoji(emojiTarget.tabId, emoji);
        setGroupEmojis((prev) => ({ ...prev, [emojiTarget.tabId]: emoji }));
      } else {
        // Single display emoji stored as emojis[0] (portal cards read the first entry)
        await updateDoc(doc(db, 'automations', emojiTarget.row.id), {
          emojis: emoji ? [emoji] : [],
          updatedAt: new Date(),
        });
        setAutomations((prev) =>
          prev.map((r) => (r.id === emojiTarget.row.id ? { ...r, emojis: emoji ? [emoji] : [] } : r))
        );
      }
      setEmojiTarget(null);
    } catch (err) {
      console.error('Emoji save failed:', err);
      setError(err.message || 'Could not save the emoji.');
    } finally {
      setEmojiSaving(false);
    }
  };

  // ---- Add template ----
  const openAdd = () => {
    setAddForm({ name: '', description: '', category: activeTab, emoji: '' });
    setAddSteps([]);
    setNewStepTitle('');
    setAddError('');
    setAddOpen(true);
  };

  const addStepToForm = () => {
    const title = newStepTitle.trim();
    if (!title) return;
    setAddSteps((prev) => [...prev, { title, emoji: '' }]);
    setNewStepTitle('');
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!addForm.name.trim()) {
      setAddError('Enter a template name.');
      return;
    }
    setSubmitting(true);
    setAddError('');
    try {
      const docRef = await addDoc(collection(db, 'automations'), {
        name: addForm.name.trim(),
        description: addForm.description.trim(),
        category: addForm.category,
        softwares: [],
        emojis: addForm.emoji ? [addForm.emoji] : [],
        version: '1.0',
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      const stepsRef = collection(db, `automations/${docRef.id}/workflowSteps`);
      for (let i = 0; i < addSteps.length; i++) {
        await addDoc(stepsRef, {
          title: addSteps[i].title,
          emoji: addSteps[i].emoji || '',
          order: i + 1,
        });
      }
      setAddOpen(false);
      if (addForm.category === activeTab) await refreshList();
      else setActiveTab(addForm.category);
    } catch (err) {
      console.error('Error creating automation:', err);
      setAddError(err.message || 'Could not create the template.');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Reorder (display order here and on the portal) ----
  const moveAutomation = async (row, direction) => {
    const idx = automations.findIndex((r) => r.id === row.id);
    const target = idx + direction;
    if (idx === -1 || target < 0 || target >= automations.length) return;
    const next = [...automations];
    [next[idx], next[target]] = [next[target], next[idx]];
    setAutomations(next.map((r, i) => ({ ...r, sortOrder: i })));
    try {
      await saveAutomationsOrder(next.map((r) => r.id));
    } catch (err) {
      console.error('Reorder failed:', err);
      await refreshList();
    }
  };

  // ---- Delete ----
  const handleDelete = async (row) => {
    if (!window.confirm(`Delete "${row.name}"? Its steps and version history are removed.`)) return;
    setError(null);
    try {
      await deleteAutomationTemplate(row.id);
      await refreshList();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Delete failed.');
    }
  };

  // ---- App connections (localStorage, read by the portal Connect Apps modal) ----
  const handleAppToggle = (appId) => {
    setSelectedApps((prev) => {
      const newSelection = prev.includes(appId)
        ? prev.filter((id) => id !== appId)
        : [...prev, appId];
      localStorage.setItem('appConnections', JSON.stringify(newSelection));
      window.dispatchEvent(
        new CustomEvent('localStorageChange', {
          detail: { key: 'appConnections', newValue: newSelection },
        })
      );
      return newSelection;
    });
  };

  const handleClearAllApps = () => {
    setSelectedApps([]);
    localStorage.removeItem('appConnections');
    window.dispatchEvent(
      new CustomEvent('localStorageChange', { detail: { key: 'appConnections', newValue: [] } })
    );
  };

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative w-full max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <BackButton onClick={() => navigate('/crm/templates')} />
        <h1 className="text-3xl font-bold tracking-tight flex-1 text-center text-gray-900 dark:text-white">
          Automation Templates
        </h1>
        <button
          onClick={() => setShowAppConnectionsModal(true)}
          className={`p-2 rounded-lg transition-all ${
            darkMode
              ? 'bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white'
              : 'bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-gray-800'
          }`}
          title="App Connections"
        >
          <Grid3X3 size={20} />
        </button>
      </div>

      {/* Group tabs (collection-style pills; the smile button on the active pill picks its emoji) */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <div
              key={tab.id}
              className={`inline-flex items-center rounded-full text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-purple-600 text-white'
                  : darkMode
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <button
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 ${isActive ? 'pr-1' : ''}`}
              >
                <span className="mr-1.5">{emojiOf(tab.id)}</span>
                {tab.label}
              </button>
              {isActive && (
                <button
                  type="button"
                  onClick={() => setEmojiTarget({ kind: 'group', tabId: tab.id })}
                  className="pl-1 pr-3 py-2 text-white/80 hover:text-white"
                  title="Change group emoji"
                >
                  <SmilePlus className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      <p className={`text-sm mb-6 ${subtle}`}>{active.blurb}</p>

      {error && (
        <div
          className={`mb-4 rounded-md border px-4 py-3 text-sm ${
            darkMode
              ? 'border-red-800 bg-red-950/50 text-red-200'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <BBALoader size={40} />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {automations.map((row, rowIndex) => {
            const menuOpen = menuKey === row.id;
            const emoji = row.emojis?.[0] || '';
            return (
              <div
                key={row.id}
                className={`${card} rounded-2xl shadow-sm p-4 flex flex-col cursor-pointer hover:shadow-lg transition-shadow`}
                onClick={() => navigate(`/crm/automation-templates/${row.id}`)}
              >
                {/* Header: emoji tile + title/description + kebab menu */}
                <div className="flex items-start gap-2.5 mb-3">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEmojiTarget({ kind: 'item', row });
                    }}
                    title="Choose an emoji for this template"
                    className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                      darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    {emoji ? (
                      <span className="text-xl leading-none">{emoji}</span>
                    ) : (
                      <Zap className={`h-5 w-5 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                    )}
                  </button>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">
                      {row.name}
                    </h3>
                    {row.description && (
                      <p className={`text-xs ${subtle} mt-0.5 line-clamp-2`}>{row.description}</p>
                    )}
                  </div>
                  <div className="relative shrink-0 flex items-center" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() => moveAutomation(row, -1)}
                      disabled={rowIndex === 0}
                      className={`p-1.5 rounded-md disabled:opacity-25 disabled:cursor-default ${
                        darkMode
                          ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700 disabled:hover:bg-transparent disabled:hover:text-gray-400'
                          : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:hover:bg-transparent disabled:hover:text-gray-400'
                      }`}
                      title="Move up"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveAutomation(row, 1)}
                      disabled={rowIndex === automations.length - 1}
                      className={`p-1.5 rounded-md disabled:opacity-25 disabled:cursor-default ${
                        darkMode
                          ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700 disabled:hover:bg-transparent disabled:hover:text-gray-400'
                          : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:hover:bg-transparent disabled:hover:text-gray-400'
                      }`}
                      title="Move down"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setMenuKey(menuOpen ? null : row.id)}
                      className={`p-1.5 rounded-md ${
                        darkMode
                          ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                          : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                      title="More options"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                    {menuOpen && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setMenuKey(null)} />
                        <div
                          className={`absolute right-0 top-8 z-20 w-44 rounded-lg shadow-lg border py-1 ${
                            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setMenuKey(null);
                              handleDelete(row);
                            }}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left ${
                              darkMode ? 'text-red-400 hover:bg-red-900/30' : 'text-red-600 hover:bg-red-50'
                            }`}
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Delete template
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Footer: version chip */}
                <div className="mt-auto flex items-center pt-1">
                  <span className={chipGood}>
                    <CheckCircle className="w-3 h-3" /> v{row.version || '1.0'}
                  </span>
                </div>
              </div>
            );
          })}

          {/* Add template tile */}
          <button
            type="button"
            onClick={openAdd}
            className={`rounded-lg border-2 border-dashed p-4 flex flex-col items-center justify-center gap-2 min-h-[120px] transition-colors ${
              darkMode
                ? 'border-gray-600 text-gray-400 hover:text-gray-200 hover:border-gray-500'
                : 'border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400'
            }`}
          >
            <Plus className="w-6 h-6" />
            <span className="text-sm font-medium">Add template</span>
          </button>
        </div>
      )}

      {/* Add template modal */}
      <Modal isOpen={addOpen} onClose={() => !submitting && setAddOpen(false)} maxWidth="md">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">Add template</h2>
        <p className={`text-sm ${subtle} mb-4`}>
          Adds a new automation template to “{TABS.find((t) => t.id === addForm.category)?.label || active.label}”.
        </p>
        <form onSubmit={submitAdd} className="space-y-4">
          <div className="flex items-start gap-3">
            <div>
              <span className={labelClass}>Emoji</span>
              <button
                type="button"
                onClick={() => setEmojiTarget({ kind: 'form' })}
                title="Choose a display emoji"
                className={`w-[42px] h-[38px] rounded-md border flex items-center justify-center transition-colors ${
                  darkMode
                    ? 'border-gray-600 bg-gray-700 hover:bg-gray-600'
                    : 'border-gray-300 bg-white hover:bg-gray-100'
                }`}
              >
                {addForm.emoji ? (
                  <span className="text-xl leading-none">{addForm.emoji}</span>
                ) : (
                  <SmilePlus className={`w-5 h-5 ${subtle}`} />
                )}
              </button>
            </div>
            <div className="flex-1">
              <label htmlFor="template-name" className={labelClass}>
                Name
              </label>
              <input
                id="template-name"
                type="text"
                value={addForm.name}
                onChange={(e) => setAddForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. New lead follow-up"
                className={inputClass}
                disabled={submitting}
                autoFocus
              />
            </div>
          </div>
          <div>
            <label htmlFor="template-description" className={labelClass}>
              Description
            </label>
            <textarea
              id="template-description"
              value={addForm.description}
              onChange={(e) => setAddForm((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="What this automation does"
              rows={2}
              className={inputClass}
              disabled={submitting}
            />
          </div>
          <div>
            <label htmlFor="template-category" className={labelClass}>
              Department
            </label>
            <select
              id="template-category"
              value={addForm.category}
              onChange={(e) => setAddForm((prev) => ({ ...prev, category: e.target.value }))}
              className={inputClass}
              disabled={submitting}
            >
              {TABS.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          {/* Workflow steps — title-only, each with its own emoji */}
          <div>
            <span className={labelClass}>Workflow steps</span>
            <div className="space-y-2">
              {addSteps.map((step, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-2 rounded-md px-2 py-1.5 ${
                    darkMode ? 'bg-gray-900/60' : 'bg-gray-50'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setEmojiTarget({ kind: 'form-step', index })}
                    title="Choose an emoji for this step"
                    className={`shrink-0 w-8 h-8 rounded-md flex items-center justify-center transition-colors ${
                      darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-white hover:bg-gray-200 border border-gray-200'
                    }`}
                  >
                    {step.emoji ? (
                      <span className="text-base leading-none">{step.emoji}</span>
                    ) : (
                      <SmilePlus className={`w-4 h-4 ${subtle}`} />
                    )}
                  </button>
                  <span className={`text-xs font-semibold ${subtle}`}>{index + 1}.</span>
                  <span className="flex-1 min-w-0 truncate text-sm text-gray-900 dark:text-white">
                    {step.title}
                  </span>
                  <button
                    type="button"
                    onClick={() => setAddSteps((prev) => prev.filter((_, i) => i !== index))}
                    className="text-red-500 hover:text-red-600 p-1"
                    title="Remove step"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newStepTitle}
                  onChange={(e) => setNewStepTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addStepToForm();
                    }
                  }}
                  placeholder="Step title"
                  className={inputClass}
                  disabled={submitting}
                />
                <button
                  type="button"
                  onClick={addStepToForm}
                  disabled={submitting || !newStepTitle.trim()}
                  className="shrink-0 px-3 py-2 rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Add
                </button>
              </div>
            </div>
          </div>

          {addError && (
            <p className="text-sm text-red-600 flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" /> {addError}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => setAddOpen(false)}
              disabled={submitting}
              className={`px-4 py-2 rounded-md text-sm ${
                darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !addForm.name.trim()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Creating…
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" /> Create
                </>
              )}
            </button>
          </div>
        </form>
      </Modal>

      {/* Emoji picker (group tabs, template cards, add form) */}
      <Modal isOpen={!!emojiTarget} onClose={() => !emojiSaving && setEmojiTarget(null)} maxWidth="xl" zIndex={100}>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1 text-center">
          Choose an emoji
        </h2>
        <p className={`text-sm mb-4 text-center ${subtle}`}>
          {emojiTarget?.kind === 'group'
            ? `${TABS.find((t) => t.id === emojiTarget.tabId)?.label || ''} group`
            : emojiTarget?.kind === 'item'
            ? emojiTarget?.row?.name
            : emojiTarget?.kind === 'form-step'
            ? addSteps[emojiTarget.index]?.title || 'Step'
            : addForm.name || 'New template'}
        </p>
        <div className="flex justify-center">
          {emojiTarget && (
            <React.Suspense
              fallback={
                <div className="flex justify-center items-center h-72 w-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
                </div>
              }
            >
              <EmojiPicker
                onEmojiClick={(emojiData) => !emojiSaving && pickEmoji(emojiData.emoji)}
                theme={darkMode ? 'dark' : 'light'}
                width="100%"
                height={380}
                lazyLoadEmojis
                previewConfig={{ showPreview: false }}
              />
            </React.Suspense>
          )}
        </div>
        <div className="flex items-center justify-between mt-4">
          {(emojiTarget?.kind === 'group'
            ? !!groupEmojis[emojiTarget.tabId]
            : emojiTarget?.kind === 'item'
            ? !!emojiTarget?.row?.emojis?.[0]
            : emojiTarget?.kind === 'form-step'
            ? !!addSteps[emojiTarget?.index]?.emoji
            : !!addForm.emoji) ? (
            <button
              onClick={() => pickEmoji('')}
              disabled={emojiSaving}
              className="px-3 py-2 rounded-md text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
            >
              Use default
            </button>
          ) : (
            <span />
          )}
          <button
            onClick={() => setEmojiTarget(null)}
            disabled={emojiSaving}
            className={`px-4 py-2 rounded-md text-sm ${
              darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
            }`}
          >
            {emojiSaving ? 'Saving…' : 'Cancel'}
          </button>
        </div>
      </Modal>

      {/* App Connections modal */}
      {showAppConnectionsModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto`}>
            <div className={`${darkMode ? 'border-gray-700' : 'border-gray-200'} border-b p-4 flex justify-between items-center sticky top-0 ${darkMode ? 'bg-gray-800' : 'bg-white'} z-10`}>
              <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                App Connections
              </h2>
              <button
                onClick={() => setShowAppConnectionsModal(false)}
                className={`rounded-full p-1 ${darkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-200 text-gray-600'}`}
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              <div className="space-y-6">
                {/* Selected Apps Display */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Connected Apps ({selectedApps.length})
                    </h3>
                    {selectedApps.length > 0 && (
                      <button
                        onClick={handleClearAllApps}
                        className="text-sm px-3 py-1 rounded-md bg-red-500 hover:bg-red-600 text-white"
                      >
                        Clear All
                      </button>
                    )}
                  </div>

                  {selectedApps.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {selectedApps.map((appId) => {
                        const app = allSoftwareTypes.find((s) => s.id === appId);
                        return app ? (
                          <div
                            key={app.id}
                            className={`flex items-center justify-between p-4 rounded-lg border ${
                              darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center space-x-3">
                              <div className={`h-10 w-10 rounded-xl flex items-center justify-center bg-gray-100 dark:bg-gray-800`}>
                                <Zap className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                              </div>
                              <div>
                                <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {app.name}
                                </h4>
                                <p className={`text-sm ${subtle}`}>{app.tier}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleAppToggle(app.id)}
                              className={`p-1 rounded-full ${darkMode ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ) : null;
                      })}
                    </div>
                  ) : (
                    <div className={`text-center py-8 ${subtle}`}>
                      <Grid3X3 size={48} className="mx-auto mb-3 opacity-50" />
                      <p>No apps connected yet</p>
                      <p className="text-sm">Select apps from the collections below to get started</p>
                    </div>
                  )}
                </div>

                {/* Available Apps by Collection */}
                <div className="space-y-6">
                  <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Available Apps
                  </h3>

                  {softwareCollections.map((coll) => (
                    <div key={coll.name} className="space-y-3">
                      <h4 className={`text-md font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {coll.name} Collection
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {coll.types.map((softwareType) => (
                          <button
                            key={softwareType.id}
                            onClick={() => handleAppToggle(softwareType.id)}
                            className={`flex items-center space-x-3 p-3 rounded-lg border transition-all ${
                              selectedApps.includes(softwareType.id)
                                ? darkMode
                                  ? 'border-purple-500 bg-purple-900/20'
                                  : 'border-purple-500 bg-purple-50'
                                : darkMode
                                ? 'border-gray-600 hover:border-gray-500 bg-gray-700 hover:bg-gray-600'
                                : 'border-gray-300 hover:border-gray-400 bg-white hover:bg-gray-50'
                            }`}
                          >
                            <div className={`h-8 w-8 rounded-xl flex items-center justify-center bg-gray-100 dark:bg-gray-800`}>
                              <Zap className={`h-4 w-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                            </div>
                            <div className="flex-1 text-left">
                              <h5
                                className={`font-medium text-sm ${
                                  selectedApps.includes(softwareType.id)
                                    ? 'text-purple-600 dark:text-purple-400'
                                    : darkMode
                                    ? 'text-white'
                                    : 'text-gray-900'
                                }`}
                              >
                                {softwareType.name}
                              </h5>
                              <p
                                className={`text-xs ${
                                  selectedApps.includes(softwareType.id)
                                    ? 'text-purple-500'
                                    : subtle
                                }`}
                              >
                                {softwareType.description}
                              </p>
                            </div>
                            {selectedApps.includes(softwareType.id) && (
                              <Check className="h-4 w-4 text-purple-500" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className={`${darkMode ? 'border-gray-700' : 'border-gray-200'} border-t p-4 flex justify-end sticky bottom-0 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <button
                onClick={() => setShowAppConnectionsModal(false)}
                className="px-4 py-2 rounded-md bg-purple-600 hover:bg-purple-700 text-white"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default AutomationTemplates;
